import React, { useState, useRef } from 'react';
import { Card, Row, Col, Statistic, Select, Space, Table, DatePicker, Typography, Button, Descriptions, Tag } from 'antd';
import { DollarOutlined, UserOutlined, BookOutlined, RiseOutlined, TeamOutlined, PrinterOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const StaffReport = () => {
    const { t } = useTranslation();
    const [selectedStaffId, setSelectedStaffId] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const reportRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
    });

    // Fetch all data
    const { data: staffData, isLoading: staffLoading } = useQuery('staff', async () => {
        const response = await api.get('/staff');
        return response.data.data;
    });

    const { data: studentsData, isLoading: studentsLoading } = useQuery('students', async () => {
        const response = await api.get('/students');
        return response.data.data;
    });

    const { data: coursesData, isLoading: coursesLoading } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    });

    const { data: incomeData, isLoading: incomeLoading } = useQuery('income', async () => {
        const response = await api.get('/income', {
            params: { limit: 10000 }
        });
        return response.data.data;
    });

    const { data: salaryData, isLoading: salaryLoading } = useQuery('salary', async () => {
        const response = await api.get('/salary');
        return response.data.data;
    });

    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${(amount || 0).toLocaleString()}`;
    };

    // Filter data by date range
    const filterByDateRange = (data, dateField = 'date') => {
        if (!dateRange || !data || !Array.isArray(data)) return data || [];
        return data.filter(item => {
            const itemDate = dayjs(item[dateField] || item.createdAt);
            return itemDate.isAfter(dateRange[0].startOf('day')) && itemDate.isBefore(dateRange[1].endOf('day'));
        });
    };

    // Get selected staff
    const selectedStaff = staffData?.find(s => s._id === selectedStaffId);

    // Calculate staff statistics
    const calculateStaffStats = () => {
        if (!selectedStaff || !studentsData || !coursesData || !incomeData || !salaryData) {
            return {
                totalSalaryUSD: 0,
                totalSalaryIQD: 0,
                totalCommissionUSD: 0,
                totalCommissionIQD: 0,
                totalIncomeUSD: 0,
                totalIncomeIQD: 0,
                studentCount: 0,
                completedCourses: 0,
                activeCourses: 0,
                salaryRecordsUSD: [],
                salaryRecordsIQD: [],
                commissionRecordsUSD: [],
                commissionRecordsIQD: [],
                coursesUSD: [],
                coursesIQD: [],
                students: []
            };
        }

        const staffId = selectedStaff._id;

        // Filter salary records for this staff in date range (separate by currency)
        const staffSalaryRecords = filterByDateRange(
            salaryData.filter(s => (s.staff?._id || s.staff) === staffId),
            'month'
        );

        const salaryRecordsUSD = staffSalaryRecords.filter(s => s.currency === 'USD');
        const salaryRecordsIQD = staffSalaryRecords.filter(s => s.currency === 'IQD');

        const totalSalaryUSD = salaryRecordsUSD.reduce((sum, record) => sum + (record.amount || 0), 0);
        const totalSalaryIQD = salaryRecordsIQD.reduce((sum, record) => sum + (record.amount || 0), 0);

        // Determine which courses to consider for commission
        let staffCourses = [];

        if (selectedStaff.commission?.enabled) {
            const appliesTo = selectedStaff.commission.appliesTo;
            const specificCourses = selectedStaff.commission.specificCourses || [];

            if (appliesTo === 'all' || appliesTo === 'all-income' || appliesTo === 'course-income') {
                // Staff gets commission on all courses
                staffCourses = coursesData || [];
            } else if (appliesTo === 'specific' || appliesTo === 'specific-courses') {
                // Staff gets commission on specific courses
                staffCourses = coursesData?.filter(course =>
                    specificCourses.includes(course._id)
                ) || [];
            }
        } else {
            // No commission enabled, only count courses where they are instructor
            staffCourses = coursesData?.filter(course => {
                const instructorId = course.instructor?._id || course.instructor;
                return instructorId === staffId;
            }) || [];
        }

        // Filter active and completed courses in date range
        const activeCourses = staffCourses.filter(c => c.isActive).length;
        const completedCoursesInRange = staffCourses.filter(c => {
            if (c.isActive) return false;
            const completedDate = dayjs(c.updatedAt);
            return completedDate.isAfter(dateRange[0]) && completedDate.isBefore(dateRange[1]);
        });
        const completedCourses = completedCoursesInRange.length;

        // Calculate total income from courses (filtered by date, separate by currency)
        let totalCourseIncomeUSD = 0;
        let totalCourseIncomeIQD = 0;
        const studentsList = [];
        const coursesListUSD = [];
        const coursesListIQD = [];

        staffCourses.forEach(course => {
            const courseId = course._id;
            let courseIncomeUSD = 0;
            let courseIncomeIQD = 0;
            let courseStudentCount = 0;

            studentsData.forEach(student => {
                student.enrollments?.forEach(enrollment => {
                    const enrollmentCourseId = enrollment.course?._id || enrollment.course;
                    if (enrollmentCourseId === courseId) {
                        // Check if payment is within date range
                        const enrollmentDate = dayjs(enrollment.enrollmentDate || student.createdAt);

                        if (enrollmentDate.isAfter(dateRange[0]) && enrollmentDate.isBefore(dateRange[1])) {
                            const paidAmount = enrollment.payment?.paidAmount || 0;
                            const currency = enrollment.payment?.currency || 'USD';

                            if (currency === 'USD') {
                                courseIncomeUSD += paidAmount;
                                totalCourseIncomeUSD += paidAmount;
                            } else {
                                courseIncomeIQD += paidAmount;
                                totalCourseIncomeIQD += paidAmount;
                            }

                            courseStudentCount++;

                            // Add to students list if not already added
                            if (!studentsList.find(s => s.studentId === student._id && s.courseCode === course.code)) {
                                studentsList.push({
                                    studentId: student._id,
                                    studentName: `${student.firstName} ${student.lastName}`,
                                    courseName: course.name,
                                    courseCode: course.code,
                                    paidAmount: paidAmount,
                                    currency: currency,
                                    enrollmentDate: enrollmentDate.format('YYYY-MM-DD'),
                                    status: enrollment.status
                                });
                            }
                        }
                    }
                });
            });

            if (courseIncomeUSD > 0) {
                coursesListUSD.push({
                    courseId: course._id,
                    courseName: course.name,
                    courseCode: course.code,
                    studentCount: courseStudentCount,
                    income: courseIncomeUSD,
                    status: course.isActive ? 'active' : 'completed'
                });
            }

            if (courseIncomeIQD > 0) {
                coursesListIQD.push({
                    courseId: course._id,
                    courseName: course.name,
                    courseCode: course.code,
                    studentCount: courseStudentCount,
                    income: courseIncomeIQD,
                    status: course.isActive ? 'active' : 'completed'
                });
            }
        });

        // Calculate commission based on staff settings (separate by currency)
        let totalCommissionUSD = 0;
        let totalCommissionIQD = 0;
        const commissionRecordsUSD = [];
        const commissionRecordsIQD = [];

        if (selectedStaff.commission?.enabled) {
            const commissionType = selectedStaff.commission.type;
            const appliesTo = selectedStaff.commission.appliesTo;
            const specificCourses = selectedStaff.commission.specificCourses || [];

            if (appliesTo === 'all') {
                // Commission on all income - USD
                if (totalCourseIncomeUSD > 0) {
                    let commissionUSD = 0;
                    if (commissionType === 'percentage') {
                        commissionUSD = (totalCourseIncomeUSD * selectedStaff.commission.percentage) / 100;
                    } else if (commissionType === 'fixed') {
                        commissionUSD = selectedStaff.commission.fixedAmount || 0;
                    }
                    totalCommissionUSD = commissionUSD;
                    commissionRecordsUSD.push({
                        source: t('allIncome'),
                        baseAmount: totalCourseIncomeUSD,
                        commissionAmount: commissionUSD,
                        type: commissionType,
                        value: commissionType === 'percentage'
                            ? `${selectedStaff.commission.percentage}%`
                            : formatCurrency(selectedStaff.commission.fixedAmount, 'USD')
                    });
                }

                // Commission on all income - IQD
                if (totalCourseIncomeIQD > 0) {
                    let commissionIQD = 0;
                    if (commissionType === 'percentage') {
                        commissionIQD = (totalCourseIncomeIQD * selectedStaff.commission.percentage) / 100;
                    } else if (commissionType === 'fixed') {
                        commissionIQD = selectedStaff.commission.fixedAmount || 0;
                    }
                    totalCommissionIQD = commissionIQD;
                    commissionRecordsIQD.push({
                        source: t('allIncome'),
                        baseAmount: totalCourseIncomeIQD,
                        commissionAmount: commissionIQD,
                        type: commissionType,
                        value: commissionType === 'percentage'
                            ? `${selectedStaff.commission.percentage}%`
                            : formatCurrency(selectedStaff.commission.fixedAmount, 'IQD')
                    });
                }
            } else if (appliesTo === 'specific') {
                // Commission on specific courses - USD
                coursesListUSD.forEach(course => {
                    if (specificCourses.includes(course.courseId)) {
                        let courseCommission = 0;

                        if (commissionType === 'percentage') {
                            courseCommission = (course.income * selectedStaff.commission.percentage) / 100;
                        } else if (commissionType === 'fixed') {
                            courseCommission = selectedStaff.commission.fixedAmount || 0;
                        }

                        totalCommissionUSD += courseCommission;
                        commissionRecordsUSD.push({
                            source: `${course.courseCode} - ${course.courseName}`,
                            baseAmount: course.income,
                            commissionAmount: courseCommission,
                            type: commissionType,
                            value: commissionType === 'percentage'
                                ? `${selectedStaff.commission.percentage}%`
                                : formatCurrency(selectedStaff.commission.fixedAmount, 'USD')
                        });
                    }
                });

                // Commission on specific courses - IQD
                coursesListIQD.forEach(course => {
                    if (specificCourses.includes(course.courseId)) {
                        let courseCommission = 0;

                        if (commissionType === 'percentage') {
                            courseCommission = (course.income * selectedStaff.commission.percentage) / 100;
                        } else if (commissionType === 'fixed') {
                            courseCommission = selectedStaff.commission.fixedAmount || 0;
                        }

                        totalCommissionIQD += courseCommission;
                        commissionRecordsIQD.push({
                            source: `${course.courseCode} - ${course.courseName}`,
                            baseAmount: course.income,
                            commissionAmount: courseCommission,
                            type: commissionType,
                            value: commissionType === 'percentage'
                                ? `${selectedStaff.commission.percentage}%`
                                : formatCurrency(selectedStaff.commission.fixedAmount, 'IQD')
                        });
                    }
                });
            }
        }

        // Total income for staff (salary + commission)
        const totalIncomeUSD = totalSalaryUSD + totalCommissionUSD;
        const totalIncomeIQD = totalSalaryIQD + totalCommissionIQD;

        return {
            totalSalaryUSD,
            totalSalaryIQD,
            totalCommissionUSD,
            totalCommissionIQD,
            totalIncomeUSD,
            totalIncomeIQD,
            studentCount: studentsList.length,
            completedCourses,
            activeCourses,
            salaryRecordsUSD: salaryRecordsUSD.map(s => ({
                month: s.month,
                amount: s.amount,
                currency: s.currency,
                status: s.status,
                paidDate: s.paidDate
            })),
            salaryRecordsIQD: salaryRecordsIQD.map(s => ({
                month: s.month,
                amount: s.amount,
                currency: s.currency,
                status: s.status,
                paidDate: s.paidDate
            })),
            commissionRecordsUSD,
            commissionRecordsIQD,
            coursesUSD: coursesListUSD,
            coursesIQD: coursesListIQD,
            students: studentsList
        };
    };

    const stats = calculateStaffStats();

    // Table columns
    const salaryColumns = [
        {
            title: t('month'),
            dataIndex: 'month',
            key: 'month',
            render: (month) => dayjs(month).format('MMMM YYYY'),
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    paid: 'green',
                    pending: 'orange',
                    overdue: 'red'
                };
                return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
            }
        },
        {
            title: t('paidDate'),
            dataIndex: 'paidDate',
            key: 'paidDate',
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
    ];

    const commissionColumns = [
        {
            title: t('source'),
            dataIndex: 'source',
            key: 'source',
        },
        {
            title: t('baseAmount'),
            dataIndex: 'baseAmount',
            key: 'baseAmount',
            render: (val) => formatCurrency(val, stats.currency),
        },
        {
            title: t('commissionType'),
            dataIndex: 'type',
            key: 'type',
            render: (type) => type === 'percentage' ? t('percentage') : t('fixedAmount'),
        },
        {
            title: t('commissionValue'),
            dataIndex: 'value',
            key: 'value',
        },
        {
            title: t('commissionAmount'),
            dataIndex: 'commissionAmount',
            key: 'commissionAmount',
            render: (val) => formatCurrency(val, stats.currency),
        },
    ];

    const courseColumns = [
        {
            title: t('courseCode'),
            dataIndex: 'courseCode',
            key: 'courseCode',
        },
        {
            title: t('courseName'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('studentCount'),
            dataIndex: 'studentCount',
            key: 'studentCount',
        },
        {
            title: t('income'),
            dataIndex: 'income',
            key: 'income',
            render: (val, record) => formatCurrency(val, record.currency || 'USD'),
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'active' ? 'green' : 'blue';
                return <Tag color={color}>{status?.toUpperCase()}</Tag>;
            }
        },
    ];

    const studentColumns = [
        {
            title: t('studentName'),
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: t('courseName'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('courseCode'),
            dataIndex: 'courseCode',
            key: 'courseCode',
        },
        {
            title: t('paidAmount'),
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('enrollmentDate'),
            dataIndex: 'enrollmentDate',
            key: 'enrollmentDate',
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    active: 'green',
                    completed: 'blue',
                    inactive: 'orange'
                };
                return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
            }
        },
    ];

    const isLoading = staffLoading || studentsLoading || coursesLoading || incomeLoading || salaryLoading;

    return (
        <div>
            <div className="page-header">
                <Title level={2}>{t('staffReport')}</Title>
                <Space>
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="YYYY-MM-DD"
                        style={{ width: 300 }}
                    />
                    <Select
                        placeholder={t('selectStaff')}
                        style={{ width: 300 }}
                        onChange={setSelectedStaffId}
                        value={selectedStaffId}
                        loading={staffLoading}
                        showSearch
                        optionFilterProp="children"
                    >
                        {staffData?.map(staff => (
                            <Option key={staff._id} value={staff._id}>
                                {staff.firstName} {staff.lastName} - {staff.position}
                            </Option>
                        ))}
                    </Select>
                    {selectedStaffId && (
                        <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                            {t('print')}
                        </Button>
                    )}
                </Space>
            </div>

            {selectedStaff && (
                <div ref={reportRef} style={{ padding: '20px' }}>
                    {/* Staff Info */}
                    <Card style={{ marginBottom: 24 }}>
                        <Descriptions title={t('staffInformation')} bordered>
                            <Descriptions.Item label={t('name')}>
                                {selectedStaff.firstName} {selectedStaff.lastName}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('position')}>
                                {selectedStaff.position}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('phone')}>
                                {selectedStaff.phone}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('email')}>
                                {selectedStaff.email || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('monthlySalary')}>
                                {formatCurrency(selectedStaff.salary?.amount, selectedStaff.salary?.currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('status')}>
                                <Tag color={selectedStaff.status === 'active' ? 'green' : 'red'}>
                                    {selectedStaff.status?.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('dateRange')} span={3}>
                                <Space>
                                    <CalendarOutlined />
                                    <Text>{dateRange[0].format('YYYY-MM-DD')} - {dateRange[1].format('YYYY-MM-DD')}</Text>
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Statistics Cards - USD */}
                    {(stats.totalSalaryUSD > 0 || stats.totalCommissionUSD > 0) && (
                        <>
                            <Title level={4} style={{ marginBottom: 16 }}>USD {t('statistics')}</Title>
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalSalary')} (USD)`}
                                            value={formatCurrency(stats.totalSalaryUSD, 'USD')}
                                            prefix={<DollarOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalCommission')} (USD)`}
                                            value={formatCurrency(stats.totalCommissionUSD, 'USD')}
                                            prefix={<RiseOutlined />}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalIncome')} (USD)`}
                                            value={formatCurrency(stats.totalIncomeUSD, 'USD')}
                                            prefix={<DollarOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Statistics Cards - IQD */}
                    {(stats.totalSalaryIQD > 0 || stats.totalCommissionIQD > 0) && (
                        <>
                            <Title level={4} style={{ marginBottom: 16 }}>IQD {t('statistics')}</Title>
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalSalary')} (IQD)`}
                                            value={formatCurrency(stats.totalSalaryIQD, 'IQD')}
                                            prefix={<DollarOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalCommission')} (IQD)`}
                                            value={formatCurrency(stats.totalCommissionIQD, 'IQD')}
                                            prefix={<RiseOutlined />}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card>
                                        <Statistic
                                            title={`${t('totalIncome')} (IQD)`}
                                            value={formatCurrency(stats.totalIncomeIQD, 'IQD')}
                                            prefix={<DollarOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* General Statistics */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={8}>
                            <Card>
                                <Statistic
                                    title={t('totalStudents')}
                                    value={stats.studentCount}
                                    prefix={<UserOutlined />}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Card>
                                <Statistic
                                    title={t('activeCourses')}
                                    value={stats.activeCourses}
                                    prefix={<BookOutlined />}
                                    valueStyle={{ color: '#13c2c2' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Card>
                                <Statistic
                                    title={t('completedCourses')}
                                    value={stats.completedCourses}
                                    prefix={<BookOutlined />}
                                    valueStyle={{ color: '#2f54eb' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Income Breakdown Charts */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        {/* Salary vs Commission Chart - USD */}
                        {(stats.totalSalaryUSD > 0 || stats.totalCommissionUSD > 0) && (
                            <Col xs={24} lg={12}>
                                <Card title={`${t('incomeBreakdown')} (USD)`}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: t('salary'), value: stats.totalSalaryUSD },
                                                    { name: t('commission'), value: stats.totalCommissionUSD }
                                                ].filter(item => item.value > 0)}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, value, percent }) => `${name}: $${value.toLocaleString()} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                <Cell fill="#1890ff" />
                                                <Cell fill="#52c41a" />
                                            </Pie>
                                            <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        )}

                        {/* Salary vs Commission Chart - IQD */}
                        {(stats.totalSalaryIQD > 0 || stats.totalCommissionIQD > 0) && (
                            <Col xs={24} lg={12}>
                                <Card title={`${t('incomeBreakdown')} (IQD)`}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: t('salary'), value: stats.totalSalaryIQD },
                                                    { name: t('commission'), value: stats.totalCommissionIQD }
                                                ].filter(item => item.value > 0)}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, value, percent }) => `${name}: IQD ${value.toLocaleString()} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                <Cell fill="#1890ff" />
                                                <Cell fill="#52c41a" />
                                            </Pie>
                                            <Tooltip formatter={(value) => `IQD ${value.toLocaleString()}`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    {/* Course Performance Chart */}
                    {(stats.coursesUSD.length > 0 || stats.coursesIQD.length > 0) && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            {stats.coursesUSD.length > 0 && (
                                <Col xs={24} lg={12}>
                                    <Card title={`${t('coursePerformance')} (USD)`}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={stats.coursesUSD.map(c => ({
                                                name: c.courseCode,
                                                students: c.studentCount,
                                                income: c.income
                                            }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis yAxisId="left" orientation="left" stroke="#1890ff" />
                                                <YAxis yAxisId="right" orientation="right" stroke="#52c41a" />
                                                <Tooltip />
                                                <Legend />
                                                <Bar yAxisId="left" dataKey="students" fill="#1890ff" name={t('students')} />
                                                <Bar yAxisId="right" dataKey="income" fill="#52c41a" name={t('income')} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            )}

                            {stats.coursesIQD.length > 0 && (
                                <Col xs={24} lg={12}>
                                    <Card title={`${t('coursePerformance')} (IQD)`}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={stats.coursesIQD.map(c => ({
                                                name: c.courseCode,
                                                students: c.studentCount,
                                                income: c.income
                                            }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis yAxisId="left" orientation="left" stroke="#1890ff" />
                                                <YAxis yAxisId="right" orientation="right" stroke="#52c41a" />
                                                <Tooltip />
                                                <Legend />
                                                <Bar yAxisId="left" dataKey="students" fill="#1890ff" name={t('students')} />
                                                <Bar yAxisId="right" dataKey="income" fill="#52c41a" name={t('income')} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    {/* Monthly Salary Trend Chart */}
                    {(stats.salaryRecordsUSD.length > 0 || stats.salaryRecordsIQD.length > 0) && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            {stats.salaryRecordsUSD.length > 0 && (
                                <Col xs={24} lg={12}>
                                    <Card title={`${t('salaryTrend')} (USD)`}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={stats.salaryRecordsUSD.map(s => ({
                                                month: dayjs(s.month).format('MMM YYYY'),
                                                amount: s.amount,
                                                status: s.status
                                            }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                                <Legend />
                                                <Line type="monotone" dataKey="amount" stroke="#1890ff" strokeWidth={2} name={t('salary')} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            )}

                            {stats.salaryRecordsIQD.length > 0 && (
                                <Col xs={24} lg={12}>
                                    <Card title={`${t('salaryTrend')} (IQD)`}>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={stats.salaryRecordsIQD.map(s => ({
                                                month: dayjs(s.month).format('MMM YYYY'),
                                                amount: s.amount,
                                                status: s.status
                                            }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip formatter={(value) => `IQD ${value.toLocaleString()}`} />
                                                <Legend />
                                                <Line type="monotone" dataKey="amount" stroke="#52c41a" strokeWidth={2} name={t('salary')} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    {/* Salary Records - USD */}
                    {stats.salaryRecordsUSD.length > 0 && (
                        <Card title={`${t('salaryRecords')} (USD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={salaryColumns}
                                dataSource={stats.salaryRecordsUSD}
                                rowKey="month"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let total = 0;
                                    pageData.forEach(({ amount }) => {
                                        total += amount || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}>
                                                    <Text strong>{formatCurrency(total, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} />
                                                <Table.Summary.Cell index={3} />
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Salary Records - IQD */}
                    {stats.salaryRecordsIQD.length > 0 && (
                        <Card title={`${t('salaryRecords')} (IQD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={salaryColumns}
                                dataSource={stats.salaryRecordsIQD}
                                rowKey="month"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let total = 0;
                                    pageData.forEach(({ amount }) => {
                                        total += amount || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}>
                                                    <Text strong>{formatCurrency(total, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} />
                                                <Table.Summary.Cell index={3} />
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Commission Records - USD */}
                    {stats.commissionRecordsUSD.length > 0 && (
                        <Card title={`${t('commissionBreakdown')} (USD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={commissionColumns}
                                dataSource={stats.commissionRecordsUSD}
                                rowKey="source"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let totalBase = 0;
                                    let totalCommission = 0;
                                    pageData.forEach(({ baseAmount, commissionAmount }) => {
                                        totalBase += baseAmount || 0;
                                        totalCommission += commissionAmount || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}>
                                                    <Text strong>{formatCurrency(totalBase, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} />
                                                <Table.Summary.Cell index={3} />
                                                <Table.Summary.Cell index={4}>
                                                    <Text strong>{formatCurrency(totalCommission, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Commission Records - IQD */}
                    {stats.commissionRecordsIQD.length > 0 && (
                        <Card title={`${t('commissionBreakdown')} (IQD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={commissionColumns}
                                dataSource={stats.commissionRecordsIQD}
                                rowKey="source"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let totalBase = 0;
                                    let totalCommission = 0;
                                    pageData.forEach(({ baseAmount, commissionAmount }) => {
                                        totalBase += baseAmount || 0;
                                        totalCommission += commissionAmount || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}>
                                                    <Text strong>{formatCurrency(totalBase, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} />
                                                <Table.Summary.Cell index={3} />
                                                <Table.Summary.Cell index={4}>
                                                    <Text strong>{formatCurrency(totalCommission, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Courses - USD */}
                    {stats.coursesUSD.length > 0 && (
                        <Card title={`${t('coursesTeaching')} (USD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={courseColumns}
                                dataSource={stats.coursesUSD}
                                rowKey="courseId"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let totalStudents = 0;
                                    let totalIncome = 0;
                                    pageData.forEach(({ studentCount, income }) => {
                                        totalStudents += studentCount || 0;
                                        totalIncome += income || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={2}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2}>
                                                    <Text strong>{totalStudents}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3}>
                                                    <Text strong>{formatCurrency(totalIncome, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4} />
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Courses - IQD */}
                    {stats.coursesIQD.length > 0 && (
                        <Card title={`${t('coursesTeaching')} (IQD)`} style={{ marginBottom: 24 }}>
                            <Table
                                columns={courseColumns}
                                dataSource={stats.coursesIQD}
                                rowKey="courseId"
                                pagination={false}
                                loading={isLoading}
                                summary={(pageData) => {
                                    let totalStudents = 0;
                                    let totalIncome = 0;
                                    pageData.forEach(({ studentCount, income }) => {
                                        totalStudents += studentCount || 0;
                                        totalIncome += income || 0;
                                    });

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={2}>
                                                    <Text strong>{t('total')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2}>
                                                    <Text strong>{totalStudents}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3}>
                                                    <Text strong>{formatCurrency(totalIncome, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4} />
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </Card>
                    )}

                    {/* Students */}
                    {stats.students.length > 0 && (
                        <Card title={t('studentsEnrolled')}>
                            <Table
                                columns={studentColumns}
                                dataSource={stats.students}
                                rowKey="studentId"
                                pagination={{ pageSize: 10 }}
                                loading={isLoading}
                            />
                        </Card>
                    )}
                </div>
            )}

            {!selectedStaff && (
                <Card>
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <TeamOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                        <Title level={4} style={{ marginTop: 16, color: '#999' }}>
                            {t('selectStaffToViewReport')}
                        </Title>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default StaffReport;
